import {
  AUTHENTICITY_REVIEW_REASONS,
  NEEDS_INPUT_REVIEW_REASONS,
  NO_DATA_REVIEW_REASONS,
} from "~constants/proofOfOwnership"
import { AISuggestionResponse } from "~shapes/proofOfOwnership"

// The visual hint the operator UI paints over an AI suggestion to speed up a
// (still manual) review:
//   - "not_processed": the document wasn't analyzed (review_reason is one of the
//     no-data reasons). Empty fields are expected, so we show a neutral notice
//     and a blank form — never an accept/reject suggestion.
//   - "reject": the AI suggests rejecting (a real invalidation_reason).
//   - "needs_input": the AI extracted data but a required field is missing
//     (shares / document date). There's nothing to one-click accept yet, so the
//     UI shows the yellow review note + an amber-highlighted form, no green box.
//   - "confirm_authenticity": the AI extracted data, but the document itself is
//     flagged as possibly not genuine (recaptured photo, handmade, suspected
//     duplicate). The UI shows one combined amber box (warning + Accept & Apply
//     All) instead of a separate yellow note above a green validate box.
//   - "validate": the AI suggests approving, with extracted data to confirm.
//     A data-bearing review_reason (e.g. LOW_CONFIDENCE) rides along so the UI
//     can show the operator what to double-check.
export type AiSuggestionHint =
  | { kind: "not_processed"; reviewReason: string }
  | { kind: "reject"; invalidationReason: string }
  | { kind: "needs_input"; reviewReason: string }
  | { kind: "confirm_authenticity"; reviewReason: string }
  | { kind: "validate"; reviewReason?: string }

// Precedence matters: a no-data review_reason wins, because there's nothing to
// reject or validate when the document was never processed. Only then do we look
// at invalidation_reason ("NONE"/""/whitespace mean "no reason" → validate).
// `state` is intentionally not branched on — every POO is reviewed here
// regardless; the next phase will narrow what reaches this queue, not how we
// paint it. Legacy responses (no review_reason) simply never hit "not_processed".
export const getAiSuggestionHint = (
  suggestion: AISuggestionResponse | null,
): AiSuggestionHint | null => {
  if (!suggestion) return null

  const reviewReason = suggestion.review_reason?.trim()
  if (reviewReason && NO_DATA_REVIEW_REASONS.has(reviewReason)) {
    return { kind: "not_processed", reviewReason }
  }

  const invalidationReason = suggestion.invalidation_reason?.trim()
  if (invalidationReason && invalidationReason !== "NONE") {
    return { kind: "reject", invalidationReason }
  }

  // A missing-field reason can't be one-click validated — the operator must enter
  // the field first. Route it to its own kind so the UI drops the green box.
  if (reviewReason && NEEDS_INPUT_REVIEW_REASONS.has(reviewReason)) {
    return { kind: "needs_input", reviewReason }
  }

  // An authenticity-gate reason gets its own combined amber box instead of a
  // note + separate green validate box.
  if (reviewReason && AUTHENTICITY_REVIEW_REASONS.has(reviewReason)) {
    return { kind: "confirm_authenticity", reviewReason }
  }

  // Any other data-bearing review_reason is forwarded so the validate UI can
  // explain why the document still needs a look. An empty review_reason (legacy /
  // clean approvals) just validates with no note.
  return reviewReason
    ? { kind: "validate", reviewReason }
    : { kind: "validate" }
}
